import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import styles from "./CommandPalette.module.css";
import { Icon } from "./Icon.js";
import { ALL_NAV_ITEMS } from "./nav.js";

/** ⌘K / Ctrl-K quick-switcher (doc 01 §1.6). Phase 1: jump-to-screen with row nav. */
export function CommandPalette({
  open,
  onClose,
  aiEnabled = false,
}: {
  open: boolean;
  onClose: () => void;
  /** AI cascade (doc 13 §2.2 row 4): module off removes the AI Agent result. */
  aiEnabled?: boolean;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const items = ALL_NAV_ITEMS.filter(
      (i) => (!i.aiGated || aiEnabled) && i.label.toLowerCase().includes(needle),
    );
    return items;
  }, [q, aiEnabled]);

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      queueMicrotask(() => inputRef.current?.focus());
    }
  }, [open]);

  if (!open) return null;

  const go = (idx: number) => {
    const item = results[idx];
    if (item) {
      navigate(item.path);
      onClose();
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close; the dialog itself is role=dialog
    <div className={styles.scrim} onMouseDown={onClose}>
      <div
        className={`card ${styles.panel}`}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.searchRow}>
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            className={styles.input}
            placeholder="Jump to a screen…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              else if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                go(active);
              }
            }}
          />
          <kbd className={styles.kbd}>esc</kbd>
        </div>
        <ul className={styles.list}>
          {results.map((item, i) => (
            <li key={item.path}>
              <button
                type="button"
                className={styles.row}
                data-active={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(i)}
              >
                <Icon name={item.icon} size={16} />
                <span>{item.label}</span>
              </button>
            </li>
          ))}
          {results.length === 0 && <li className={styles.empty}>No matches</li>}
        </ul>
      </div>
    </div>
  );
}
